<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { pdfNotesChapters } from "~/features/computer/data/pdf-notes.chapters";

usePageSeo({
  title: "11th Class Computer Notes PDF - Chapter Wise Printable Pages",
  description:
    "11th class computer notes PDF hub with chapter-wise printable pages, solved exercises, important questions and PDF-style revision flow.",
  keywords:
    "11th class computer notes pdf, chapter wise computer notes pdf, class 11 solved exercises pdf, computer notes printable",
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Notes PDF Hub",
    items: pdfNotesChapters.map((chapter) => ({
      name: `${chapter.chapterLabel} ${chapter.title} PDF`,
      path: `/11th/computer/notes-pdf/${chapter.slug}/`,
    })),
  }),
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Notes PDF", item: "/11th/computer/notes-pdf/" },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[28px] border border-stone-200 bg-gradient-to-br from-stone-50 via-white to-sky-50 px-5 py-6 shadow-sm md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-700">Printable Study Hub</p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">11th Class Computer Notes PDF</h1>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-700 md:text-base">
        Yeh chapter-by-chapter PDF-intent hub hai. Har chapter ka apna document-style page hai jahan above-fold access, printable layout, solved exercises aur important questions aik hi route me milte hain.
      </p>
    </section>

    <section class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <article
        v-for="chapter in pdfNotesChapters"
        :key="chapter.slug"
        class="rounded-[24px] border border-stone-200 bg-white p-5 shadow-sm"
      >
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">{{ chapter.chapterLabel }}</p>
        <h2 class="mt-3 text-xl font-semibold tracking-tight text-slate-950">{{ chapter.title }}</h2>
        <p class="mt-3 text-sm leading-7 text-slate-700">{{ chapter.overview }}</p>

        <div class="mt-4 flex flex-wrap gap-2">
          <NuxtLink
            :to="`/11th/computer/notes-pdf/${chapter.slug}/`"
            class="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
          >
            View PDF
          </NuxtLink>
          <NuxtLink
            :to="`/11th/computer/notes-pdf/${chapter.slug}/`"
            class="inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700"
          >
            Open Chapter
          </NuxtLink>
        </div>

        <div class="mt-5 border-t border-dashed border-stone-200 pt-4">
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Includes</p>
          <div class="mt-3 flex flex-wrap gap-2">
            <span class="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-slate-700">Full Notes</span>
            <span class="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-slate-700">Solved Exercises</span>
            <span class="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-slate-700">Important Questions</span>
          </div>
        </div>
      </article>
    </section>

    <section class="rounded-[28px] border border-stone-200 bg-stone-50 px-5 py-6 md:px-8">
      <h2 class="text-2xl font-semibold tracking-tight text-slate-950">Also Open</h2>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          to="/11th/computer/notes/"
          class="rounded-[22px] border border-stone-200 bg-white p-5 transition hover:border-sky-300 hover:shadow-sm"
        >
          <h3 class="text-lg font-semibold text-slate-950">Full Book Notes</h3>
          <p class="mt-2 text-sm leading-7 text-slate-700">Chapter-wise notes index jahan se puri book ka study flow khulta hai.</p>
        </NuxtLink>
        <NuxtLink
          to="/11th/computer/paper-pattern/"
          class="rounded-[22px] border border-stone-200 bg-white p-5 transition hover:border-sky-300 hover:shadow-sm"
        >
          <h3 class="text-lg font-semibold text-slate-950">Model Papers PDF</h3>
          <p class="mt-2 text-sm leading-7 text-slate-700">Paper pattern aur final prep route ke liye exam-focused pages.</p>
        </NuxtLink>
        <NuxtLink
          to="/11th/computer/short-questions/"
          class="rounded-[22px] border border-stone-200 bg-white p-5 transition hover:border-sky-300 hover:shadow-sm"
        >
          <h3 class="text-lg font-semibold text-slate-950">Important Questions</h3>
          <p class="mt-2 text-sm leading-7 text-slate-700">Board-focused short prep ko fast revise karne ke liye direct route.</p>
        </NuxtLink>
      </div>
    </section>
  </PageContainer>
</template>
